import { metadataMatchesConversation } from "@/lib/conversation-frame-node";
import { conversationFrameCopy } from "@/lib/conversation-frame-copies";
import { bindConversationFrameMetadata } from "@/lib/conversation-frame-surface";
import { useAgentStore } from "@/stores/agent-store";
import { useFramesStore } from "@/stores/frames-store";
import { useProjectStore } from "@/stores/project-store";
import { selectFramesByType } from "@/stores/frames-selectors";
import {
  type InputBarRoutingIntent,
  type ReportedRuntime,
  reportedRuntimeFields,
  useInputBarTarget,
} from "@/stores/input-bar-target-store";
import { translate } from "@/i18n";
import { libChatMessages } from "@/i18n/messages/lib-chat";

/** The backend moved a request on a different topic to a new conversation. */
export interface ConversationSplit {
  from: string;
  to: string;
  pickup_from: string | null;
}

interface AcceptedConversationIdentity {
  client_request_id: string | null;
  conversation_id?: string;
  runtime_session_id?: string;
  execution_id?: string;
  adapter_type?: string | null;
  model?: string | null;
  thinking_effort?: string | null;
  runtime_source?: string | null;
  conversation_split?: ConversationSplit;
}

function receiptRuntime(
  receipt: AcceptedConversationIdentity,
): ReportedRuntime {
  return {
    adapterType: receipt.adapter_type,
    model: receipt.model,
    thinkingEffort: receipt.thinking_effort,
    runtimeSource: receipt.runtime_source,
  };
}

function metadataString(
  metadata: Record<string, unknown> | undefined,
  ...keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = metadata?.[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return undefined;
}

export function adoptAcceptedNewConversationRoute(
  receipt: AcceptedConversationIdentity,
  intent: InputBarRoutingIntent | null,
): boolean {
  if (
    !intent?.startNewConversation ||
    !intent.intentId ||
    receipt.client_request_id !== intent.intentId ||
    !receipt.conversation_id ||
    useInputBarTarget.getState().intentId !== intent.intentId
  ) {
    return false;
  }

  const frameId = intent.newConversationFrameId ?? undefined;
  const metadata = frameId
    ? (useFramesStore.getState().framesById[frameId]?.metadata ??
      conversationFrameCopy(frameId)?.metadata)
    : undefined;
  const agents = useAgentStore.getState();
  const projectAgentId =
    intent.newConversationAgentId ??
    metadataString(metadata, "projectAgentId", "project_agent_id") ??
    agents.selectedAgentId;
  if (!projectAgentId) return false;
  const agent = agents.agents.find(
    (candidate) => candidate.id === projectAgentId,
  );
  const agentName =
    agent?.template_name ||
    metadataString(metadata, "agentName", "agent_name") ||
    "Agent";
  const agentKey =
    metadataString(metadata, "agentKey", "agent_key") ||
    agent?.template_name.toLowerCase();

  if (frameId) {
    void bindConversationFrameMetadata({
      frameId,
      conversationId: receipt.conversation_id,
      runtimeSessionId: receipt.runtime_session_id,
      executionId: receipt.execution_id,
      projectAgentId,
      agentKey,
      agentName,
    }).catch(() => {});
  }

  // A New chat without a frame starts in Home; a frame's starts in the
  // frame's project, which the receipt does not name.
  const homeProjectId = frameId
    ? undefined
    : useProjectStore.getState().projects.find((project) => project.is_home)
        ?.id;
  useInputBarTarget.getState().setTarget({
    kind: "chat",
    conversationId: receipt.conversation_id,
    runtimeSessionId: receipt.runtime_session_id ?? null,
    ...reportedRuntimeFields(receiptRuntime(receipt)),
    projectAgentId,
    agentKey,
    agentName,
    ticket: null,
    conversationLabel: translate(libChatMessages, "newChat"),
    ...(homeProjectId
      ? { projectId: homeProjectId, spaceId: homeProjectId }
      : {}),
  });
  return true;
}

/**
 * The frames showing the previous conversation and an input bar still aimed
 * at it move to the new one together, from the sender's receipt or, on every
 * other client, from `conversations.changed`. A bar the user pointed elsewhere since
 * stays put; a targetless bar already follows the moved manual selection.
 */
export function adoptConversationSplit(
  receipt: AcceptedConversationIdentity,
): boolean {
  const split = receipt.conversation_split;
  if (!split || receipt.conversation_id !== split.to) return false;
  for (const frame of selectFramesByType(
    useFramesStore.getState(),
    "conversation",
  )) {
    if (
      !metadataMatchesConversation(frame.metadata ?? {}, {
        conversationId: split.from,
      })
    )
      continue;
    void bindConversationFrameMetadata({
      frameId: frame.frameId,
      conversationId: split.to,
      runtimeSessionId: receipt.runtime_session_id,
      executionId: receipt.execution_id,
    }).catch(() => {});
  }
  const target = useInputBarTarget.getState().target;
  // The broadcast can beat the sender's receipt; the receipt then adds the runtime.
  const runtimeArrived =
    target?.conversationId === split.to &&
    !!receipt.runtime_session_id &&
    target.runtimeSessionId !== receipt.runtime_session_id;
  if (target && (target.conversationId === split.from || runtimeArrived)) {
    useInputBarTarget.getState().setTarget({
      ...(runtimeArrived ? target : {}),
      kind: "chat",
      conversationId: split.to,
      runtimeSessionId: receipt.runtime_session_id ?? null,
      ...reportedRuntimeFields(receiptRuntime(receipt)),
      projectAgentId: target.projectAgentId,
      agentKey: target.agentKey,
      agentName: target.agentName,
      ticket: null,
      conversationLabel: translate(libChatMessages, "newChat"),
      projectId: target.projectId,
      projectName: target.projectName,
      spaceId: target.spaceId,
    });
  }
  return true;
}

export function adoptAcceptedContinuationRuntime(
  receipt: AcceptedConversationIdentity,
  intent: InputBarRoutingIntent | null,
): boolean {
  const submittedTarget = intent?.target;
  if (
    !submittedTarget ||
    !receipt.conversation_id ||
    receipt.conversation_id !== submittedTarget.conversationId ||
    !receipt.runtime_session_id
  ) {
    return false;
  }
  return useInputBarTarget.getState().adoptAcceptedRuntime(intent, {
    runtimeSessionId: receipt.runtime_session_id,
    ...receiptRuntime(receipt),
  });
}
